import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useGeoCountry } from '../contexts/GeoCountryContext';
import { FooterLogo } from './Logo';
import { MapPin, Facebook, Instagram, Twitter, Mail, Phone, FlaskConical, ShieldCheck } from 'lucide-react';

const ADMIN_EMAILS = ['info@penlabpeptide.com', 'info@penpeptido.com'];

// Custom Link component that scrolls to top when clicked
const ScrollToTopLink = ({ to, children, className }) => {
  const navigate = useNavigate();
  
  const handleClick = (e) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'instant' });
    navigate(to);
  };
  
  return (
    <a href={to} onClick={handleClick} className={className}>
      {children}
    </a>
  );
};

const Footer = () => {
  const { t, currentLanguage } = useLanguage();
  const geo = useGeoCountry();
  const { user, isAuthenticated } = useAuth();
  const isAdmin = isAuthenticated && user && ADMIN_EMAILS.includes(user.email);
  const adminLabel = currentLanguage === 'es' ? 'Admin'
                   : currentLanguage === 'ru' ? 'Админ'
                   : 'Admin';

  return (
    <footer className="bg-gray-900 text-white relative overflow-hidden">
      {/* Decorative DNA element */}
      <div className="absolute top-0 right-0 w-64 h-64 opacity-5">
        <svg viewBox="0 0 200 200" fill="none" className="w-full h-full">
          <path d="M50 0 Q120 50 50 100 Q-20 150 50 200" stroke="currentColor" strokeWidth="4" fill="none"/>
          <path d="M150 0 Q80 50 150 100 Q220 150 150 200" stroke="currentColor" strokeWidth="4" fill="none"/>
        </svg>
      </div>
      
      {/* Gradient top border */}
      <div className="h-1 bg-gradient-to-r from-capsule-teal via-capsule-aqua to-capsule-coral"></div>
      
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 py-12 md:py-16">
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="col-span-2 md:col-span-1 space-y-6">
            <div>
              <FooterLogo className="mb-4" />
              <p className="text-gray-400 text-sm leading-relaxed">
                {t('footer_company_desc')}
              </p>
            </div>
            
            {/* Contact Info */}
            <div className="space-y-3">
              <div className="flex items-center space-x-3 text-gray-400">
                <div className="w-8 h-8 rounded-xl bg-capsule-teal/20 flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-capsule-teal" />
                </div>
                <span className="text-sm" data-testid="footer-location">
                  {(geo?.country_code && geo?.names?.[currentLanguage]) || (
                    currentLanguage === 'es' ? 'A nivel mundial'
                    : currentLanguage === 'ru' ? 'По всему миру'
                    : 'Worldwide'
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-lg mb-4 md:mb-6 flex items-center gap-2">
              <FlaskConical className="w-4 h-4 text-capsule-teal" />
              {t('footer_quick_links')}
            </h3>
            <ul className="space-y-2 md:space-y-3">
              <li>
                <ScrollToTopLink to="/" className="text-gray-400 hover:text-capsule-teal transition-colors text-sm flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-capsule-teal/50"></span>
                  {t('nav_home')}
                </ScrollToTopLink>
              </li>
              <li>
                <ScrollToTopLink to="/products" className="text-gray-400 hover:text-capsule-teal transition-colors text-sm flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-capsule-teal/50"></span>
                  {t('nav_products')}
                </ScrollToTopLink>
              </li>
              <li>
                <ScrollToTopLink to="/peptides" className="text-gray-400 hover:text-capsule-teal transition-colors text-sm flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-capsule-teal/50"></span>
                  {t('nav_about_peptides')}
                </ScrollToTopLink>
              </li>
              <li>
                <ScrollToTopLink to="/blog" className="text-gray-400 hover:text-capsule-teal transition-colors text-sm flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-capsule-teal/50"></span>
                  {t('nav_blog')}
                </ScrollToTopLink>
              </li>
              <li>
                <ScrollToTopLink
                  to="/calculators/reconstitution"
                  className="text-gray-400 hover:text-capsule-teal transition-colors text-sm flex items-center gap-2"
                  data-testid="footer-reconstitution-calculator-link"
                >
                  <span className="w-1 h-1 rounded-full bg-capsule-teal/50"></span>
                  {t('nav_reconstitution_calculator')}
                </ScrollToTopLink>
              </li>
              <li>
                <ScrollToTopLink to="/contact" className="text-gray-400 hover:text-capsule-teal transition-colors text-sm flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-capsule-teal/50"></span>
                  {t('nav_contact')}
                </ScrollToTopLink>
              </li>
            </ul>
          </div>

          {/* Information */}
          <div>
            <h3 className="font-semibold text-lg mb-4 md:mb-6">{t('footer_information')}</h3>
            <ul className="space-y-2 md:space-y-3">
              <li>
                <ScrollToTopLink to="/about" className="text-gray-400 hover:text-capsule-aqua transition-colors text-sm flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-capsule-aqua/50"></span>
                  {t('nav_about')}
                </ScrollToTopLink>
              </li>
              <li>
                <ScrollToTopLink to="/technology" className="text-gray-400 hover:text-capsule-aqua transition-colors text-sm flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-capsule-aqua/50"></span>
                  {t('nav_technology')}
                </ScrollToTopLink>
              </li>
              <li>
                <ScrollToTopLink to="/faqs" className="text-gray-400 hover:text-capsule-aqua transition-colors text-sm flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-capsule-aqua/50"></span>
                  {t('nav_faqs')}
                </ScrollToTopLink>
              </li>
              <li>
                <ScrollToTopLink to="/distributor" className="text-gray-400 hover:text-capsule-aqua transition-colors text-sm flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-capsule-aqua/50"></span>
                  {t('nav_distributor')}
                </ScrollToTopLink>
              </li>
              <li>
                <ScrollToTopLink to="/affiliates" className="text-gray-400 hover:text-capsule-aqua transition-colors text-sm flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-capsule-aqua/50"></span>
                  {t('footer_affiliates')}
                </ScrollToTopLink>
              </li>
              {isAdmin && (
                <li>
                  <ScrollToTopLink to="/uptime" className="text-gray-400 hover:text-capsule-aqua transition-colors text-sm flex items-center gap-2" data-testid="footer-status-link">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                    {t('footer_system_status')}
                  </ScrollToTopLink>
                </li>
              )}
            </ul>
          </div>

          {/* Newsletter & Social */}
          <div className="col-span-2 md:col-span-1">
            <h3 className="font-semibold text-lg mb-4 md:mb-6">{t('footer_follow_us')}</h3>
            <p className="text-gray-400 text-sm mb-4 md:mb-6">
              {t('footer_stay_updated')}
            </p>
            
            {/* Social Links */}
            <div className="flex space-x-3">
              <a href="#" className="w-10 h-10 bg-gray-800 rounded-2xl flex items-center justify-center hover:bg-gradient-to-br hover:from-capsule-teal hover:to-capsule-aqua transition-all duration-300 hover:scale-110">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 bg-gray-800 rounded-2xl flex items-center justify-center hover:bg-gradient-to-br hover:from-capsule-coral hover:to-capsule-salmon transition-all duration-300 hover:scale-110">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 bg-gray-800 rounded-2xl flex items-center justify-center hover:bg-gradient-to-br hover:from-capsule-aqua hover:to-capsule-teal transition-all duration-300 hover:scale-110">
                <Twitter className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-6 md:py-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-sm text-gray-500 text-center md:text-left">
              {t('footer_copyright').replace('{year}', new Date().getFullYear())}
            </div>
            
            {/* Legal Links */}
            <div className="flex flex-wrap justify-center gap-4 md:gap-6 text-sm">
              <ScrollToTopLink to="/privacy" className="text-gray-500 hover:text-capsule-teal transition-colors">
                {t('footer_privacy')}
              </ScrollToTopLink>
              <ScrollToTopLink to="/terms" className="text-gray-500 hover:text-capsule-teal transition-colors">
                {t('footer_terms')}
              </ScrollToTopLink>
              <ScrollToTopLink
                to="/return-policy"
                className="text-gray-500 hover:text-capsule-teal transition-colors"
                data-testid="footer-return-policy-link"
              >
                {t('footer_return_policy')}
              </ScrollToTopLink>
              <ScrollToTopLink
                to="/shipping-policy"
                className="text-gray-500 hover:text-capsule-teal transition-colors"
                data-testid="footer-shipping-policy-link"
              >
                {t('footer_shipping_policy')}
              </ScrollToTopLink>
              {isAdmin && (
                <ScrollToTopLink
                  to="/admin"
                  className="text-capsule-teal hover:text-capsule-aqua transition-colors font-semibold inline-flex items-center gap-1"
                  data-testid="footer-admin-link"
                >
                  <ShieldCheck className="w-3.5 h-3.5" />
                  {adminLabel}
                </ScrollToTopLink>
              )}
              <ScrollToTopLink
                to="/cookie-policy"
                className="text-gray-500 hover:text-capsule-teal transition-colors"
                data-testid="footer-cookies-link"
              >
                {t('footer_cookies')}
              </ScrollToTopLink>
            </div>
          </div>
          
          {/* SEO-Rich Footer Text */}
          <div className="mt-6 pt-6 border-t border-gray-800">
            <div className="text-center mb-6">
              <p className="text-gray-500 text-xs leading-relaxed max-w-4xl mx-auto">
                {(() => {
                  const lang = currentLanguage;
                  const inPhrase = geo?.in_phrase?.[lang] || geo?.in_phrase?.en || 'worldwide';
                  const S = (en) => <strong className="text-gray-400">{en}</strong>;
                  // 9-language SEO blurb. {S(...)} placeholders are bold inline accents.
                  const COPY = {
                    en: <><>{S('PenLab Peptide')} is the leading supplier of {S('research peptides')} {inPhrase} and worldwide. We offer 143+ premium products including {S('SARMs')}, {S('bioregulators')}, {S('NAD+ boosters')}, {S('BPC-157')}, growth peptides and more. All formulations are stabilized in pen, vial, and nasal spray formats with {'>'}99% purity guaranteed. For licensed laboratories and institutions only. High-quality peptides for scientific research.</></>,
                    es: <><>{S('PenLab Peptide')} es el proveedor líder de {S('péptidos de investigación')} {inPhrase} y a nivel mundial. Ofrecemos más de 143 productos premium incluyendo {S('SARMs')}, {S('biorreguladores')}, {S('potenciadores NAD+')}, {S('BPC-157')}, péptidos de crecimiento y más. Todas nuestras formulaciones están estabilizadas en formato pluma, vial y spray nasal con {'>'}99% de pureza garantizada. Solo para laboratorios e instituciones autorizadas. Péptidos de alta calidad para investigación científica.</></>,
                    ru: <><>{S('PenLab Peptide')}, ведущий поставщик {S('исследовательских пептидов')} {inPhrase} и по всему миру. Мы предлагаем более 143 премиальных продуктов, включая {S('SARMs')}, {S('биорегуляторы')}, {S('бустеры NAD+')}, {S('BPC-157')}, пептиды роста и многое другое. Все формулы стабилизированы в форматах ручка, флакон и назальный спрей с гарантией чистоты {'>'}99%. Только для лицензированных лабораторий и учреждений. Высококачественные пептиды для научных исследований.</></>,
                    de: <><>{S('PenLab Peptide')} ist der führende Anbieter von {S('Forschungspeptiden')} {inPhrase} und weltweit. Wir bieten über 143 Premium-Produkte, darunter {S('SARMs')}, {S('Bioregulatoren')}, {S('NAD+-Booster')}, {S('BPC-157')}, Wachstumspeptide und mehr. Alle Formulierungen sind in Pen-, Vial- und Nasenspray-Formaten mit garantierter Reinheit von {'>'}99 % stabilisiert. Ausschließlich für lizenzierte Labore und Institutionen. Hochwertige Peptide für die wissenschaftliche Forschung.</></>,
                    fr: <><>{S('PenLab Peptide')} est le principal fournisseur de {S('peptides de recherche')} {inPhrase} et dans le monde entier. Nous proposons plus de 143 produits premium dont les {S('SARMs')}, {S('biorégulateurs')}, {S('boosters NAD+')}, {S('BPC-157')}, peptides de croissance et plus. Toutes les formulations sont stabilisées en stylos, flacons et sprays nasaux avec une pureté garantie de {'>'}99 %. Réservé aux laboratoires et institutions agréés. Peptides de haute qualité pour la recherche scientifique.</></>,
                    it: <><>{S('PenLab Peptide')} è il fornitore leader di {S('peptidi di ricerca')} {inPhrase} e in tutto il mondo. Offriamo oltre 143 prodotti premium, inclusi {S('SARMs')}, {S('bioregolatori')}, {S('booster NAD+')}, {S('BPC-157')}, peptidi della crescita e altro. Tutte le formulazioni sono stabilizzate in formato penna, flaconcino e spray nasale con purezza garantita {'>'}99%. Solo per laboratori e istituzioni autorizzati. Peptidi di alta qualità per la ricerca scientifica.</></>,
                    pl: <><>{S('PenLab Peptide')} jest wiodącym dostawcą {S('peptydów badawczych')} {inPhrase} i na całym świecie. Oferujemy ponad 143 produkty premium, w tym {S('SARM')}, {S('bioregulatory')}, {S('boostery NAD+')}, {S('BPC-157')}, peptydy wzrostu i więcej. Wszystkie preparaty są stabilizowane w formatach pen, fiolki i sprayu do nosa z gwarantowaną czystością {'>'}99%. Wyłącznie dla licencjonowanych laboratoriów i instytucji. Wysokiej jakości peptydy do badań naukowych.</></>,
                    nl: <><>{S('PenLab Peptide')} is de toonaangevende leverancier van {S('onderzoekspeptiden')} {inPhrase} en wereldwijd. Wij bieden meer dan 143 premium producten, waaronder {S('SARMs')}, {S('bioregulatoren')}, {S('NAD+-boosters')}, {S('BPC-157')}, groei-peptiden en meer. Alle formuleringen zijn gestabiliseerd in pen-, vial- en neussprayformaat met gegarandeerd {'>'}99% zuiverheid. Uitsluitend voor erkende laboratoria en instellingen. Hoogwaardige peptiden voor wetenschappelijk onderzoek.</></>,
                    pt: <><>{S('PenLab Peptide')} é o principal fornecedor de {S('peptídeos de pesquisa')} {inPhrase} e em todo o mundo. Oferecemos mais de 143 produtos premium, incluindo {S('SARMs')}, {S('biorreguladores')}, {S('potenciadores NAD+')}, {S('BPC-157')}, peptídeos de crescimento e mais. Todas as formulações são estabilizadas em formatos caneta, frasco e spray nasal com pureza {'>'}99 % garantida. Apenas para laboratórios e instituições licenciados. Peptídeos de alta qualidade para pesquisa científica.</></>,
                  };
                  return COPY[lang] || COPY.en;
                })()}
              </p>
            </div>
          </div>
          
          {/* Research Disclaimer */}
          <div className="mt-4 pt-4 border-t border-gray-800">
            <div className="bg-gradient-to-r from-capsule-teal/10 via-capsule-aqua/10 to-capsule-coral/10 border border-capsule-teal/20 rounded-2xl p-4">
              <p className="text-gray-400 text-xs md:text-sm text-center flex items-center justify-center gap-2 flex-wrap">
                <FlaskConical className="w-4 h-4 text-capsule-teal" />
                <strong className="text-capsule-teal">{t('footer_disclaimer')}</strong> 
                <span>{t('research_disclaimer')}</span>
                <span>{t('footer_disclaimer_text')}</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
